import { isAddress, parseEther, type Address } from "viem";

export type AllowlistInput = { wallet: Address; maxQty: number; priceWei?: string };

export type ParsedAllowlist = {
  entries: AllowlistInput[];
  errors: string[];
};

/**
 * Parse the bulk-entry textarea: one entry per non-empty line, comma /
 * semicolon / whitespace separated:
 *
 *     <wallet> <maxQty> [<priceETH>]
 *
 * Lines starting with `#` are treated as comments. Returns successful
 * entries plus a per-line error array — invalid lines are skipped, not
 * fatal, so the user can fix them and re-submit.
 */
export function parseAllowlistBulk(text: string): ParsedAllowlist {
  const entries: AllowlistInput[] = [];
  const errors: string[] = [];
  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]!.trim();
    if (!raw || raw.startsWith("#")) continue;
    const parts = raw.split(/[,;\s]+/).filter(Boolean);
    const [walletStr, qtyStr, priceStr] = parts;
    if (!walletStr || !isAddress(walletStr)) {
      errors.push(`L${i + 1}: invalid address "${walletStr ?? ""}"`);
      continue;
    }
    const qty = Number(qtyStr ?? "1");
    if (!Number.isInteger(qty) || qty <= 0 || qty > 10_000) {
      errors.push(`L${i + 1}: invalid qty "${qtyStr ?? ""}"`);
      continue;
    }
    let priceWei: string | undefined;
    if (priceStr) {
      try {
        priceWei = parseEther(priceStr).toString();
      } catch {
        errors.push(`L${i + 1}: invalid price "${priceStr}"`);
        continue;
      }
    }
    entries.push({
      wallet: walletStr.toLowerCase() as Address,
      maxQty: qty,
      ...(priceWei ? { priceWei } : {}),
    });
  }
  return { entries, errors };
}
